"use client";

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Fingerprint, LockKeyhole, ShieldCheck } from "lucide-react";
import { defaultAdminPath, PulseLogo } from "@/components/pulse-shell";
import { api } from "@/lib/api";
import { adminSignIn, getAuthProfile } from "@/lib/auth";
import { formatApiError } from "@/lib/users-rbac";

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);

  async function signIn(e: React.FormEvent) {
    e.preventDefault();
    if (!email.includes("@") || password.length < 6) {
      setError("Enter a valid work email and a password with at least 6 characters.");
      return;
    }
    setError("");
    setInfo("");
    setBusy(true);
    try {
      await adminSignIn(email.trim(), password);
      const next = params.get("next");
      const safe =
        next && next.startsWith("/") && !next.startsWith("//") ? next : null;
      if (safe) {
        navigate(safe);
      } else {
        const profile = await getAuthProfile();
        navigate(defaultAdminPath(profile?.permissions), { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  }

  async function requestReset(e: React.FormEvent) {
    e.preventDefault();
    const target = forgotEmail.trim() || email.trim();
    if (!target.includes("@")) {
      setError("Enter a valid work email.");
      return;
    }
    setError("");
    setInfo("");
    setForgotBusy(true);
    try {
      const out = await api.auth.requestPasswordReset(target);
      setForgotOpen(false);
      setInfo(out.message || "If an account exists for that email, a reset link was sent.");
    } catch (err) {
      setError(formatApiError(err, "Could not request password reset"));
    } finally {
      setForgotBusy(false);
    }
  }

  return (
    <main className="login-page login-page--admin">
      <section className="login-visual login-visual--admin" aria-label="Operations">
        <PulseLogo />
        <div className="login-copy">
          <div className="eyebrow" style={{ color: "#5798e8" }}>Agent operations console</div>
          <h1>
            Run every
            <br />
            <span>live session.</span>
          </h1>
          <p>
            Oversee capacity, review recordings, publish agents, and keep the realtime stack healthy from one command center.
          </p>
          <div className="login-feature"><i><ShieldCheck size={14} /></i>Admin-only Cognito access</div>
          <div className="login-feature"><i><Fingerprint size={14} /></i>Session & perception evidence</div>
          <div className="login-feature"><i><LockKeyhole size={14} /></i>Encrypted artifacts in your vault</div>
        </div>
        <div className="login-art">
          <span className="beam" />
          <div className="pulse">
            {Array.from({ length: 41 }).map((_, i) => (
              <i key={i} style={{ height: 16 + Math.abs(Math.sin(i * 0.64) * 80) }} />
            ))}
          </div>
        </div>
        <div className="login-footer">
          <span>© 2026 Igentify Pulse Operations</span>
          <span>Privacy · Security · Status</span>
        </div>
      </section>
      <section className="login-form-side">
        <form className="login-panel" onSubmit={(e) => void signIn(e)}>
          <div className="eyebrow">Administrator access</div>
          <h2>Welcome back</h2>
          <p>Sign in to the Igentify Pulse Agent Operations workspace.</p>
          <label className="login-field">
            Work email
            <div className="login-input-wrap">
              <input
                aria-label="Work email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="you@company.com"
                autoComplete="username"
              />
            </div>
          </label>
          <label className="login-field">
            <span className="login-field-label-row">
              Password
              <button
                type="button"
                className="login-forgot-link"
                onClick={() => {
                  setForgotEmail(email);
                  setForgotOpen(true);
                  setError("");
                  setInfo("");
                }}
              >
                Forgot password?
              </button>
            </span>
            <div className="login-input-wrap">
              <input
                aria-label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={show ? "text" : "password"}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
              <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"}>
                {show ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </label>
          {info && <div className="login-info" role="status">{info}</div>}
          {error && <div className="login-error" role="alert">{error}</div>}
          <button className="login-submit" type="submit" disabled={busy}>
            {busy ? "Signing in…" : "Sign in securely"}
          </button>
          <div className="trust-row">
            <span><ShieldCheck size={12} />SOC 2 Type II</span>
            <span><LockKeyhole size={12} />AES-256 encrypted</span>
            <span><Fingerprint size={12} />Admin role required</span>
          </div>
        </form>
        {forgotOpen && (
          <div className="login-forgot-overlay" role="presentation" onMouseDown={() => setForgotOpen(false)}>
            <form
              className="login-forgot-panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="forgot-title"
              onMouseDown={(e) => e.stopPropagation()}
              onSubmit={(e) => void requestReset(e)}
            >
              <h3 id="forgot-title">Reset your password</h3>
              <p>Enter your work email. If an account exists, we will send a secure reset link.</p>
              <label className="login-field">
                Work email
                <div className="login-input-wrap">
                  <input
                    aria-label="Work email for reset"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
              </label>
              <div className="login-forgot-actions">
                <button type="button" className="btn ghost" onClick={() => setForgotOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn primary" disabled={forgotBusy}>
                  {forgotBusy ? "Sending…" : "Send reset link"}
                </button>
              </div>
            </form>
          </div>
        )}
      </section>
    </main>
  );
}
